/**
 * BlockTypePicker: the add-block UI.
 *
 * P5 wave 3a (admin content editor). A modal that lists all 15 block types
 * (grouped Authored / Dynamic) as selectable cards. Selecting one calls
 * `onSelect(type)` and closes; the ContentEditorPage then inserts a fresh
 * block of that type.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Col, Card } from 'react-bootstrap';

import { BLOCK_TYPE_LIST } from './blockTypes';

function BlockTypeCard({ descriptor, onSelect }) {
    return (
        <Col xs={12} sm={6} className="mb-3">
            <Card
                role="button"
                tabIndex={0}
                className="h-100 content-editor-picker__card"
                onClick={() => onSelect(descriptor.type)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(descriptor.type);
                    }
                }}
            >
                <Card.Body className="d-flex">
                    <span
                        className="me-3 fs-3 content-editor-picker__icon"
                        aria-hidden="true"
                    >
                        {descriptor.icon}
                    </span>
                    <span>
                        <strong className="d-block">{descriptor.label}</strong>
                        <small className="text-muted">
                            {descriptor.description}
                        </small>
                    </span>
                </Card.Body>
            </Card>
        </Col>
    );
}

BlockTypeCard.propTypes = {
    descriptor: PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        icon: PropTypes.string.isRequired,
    }).isRequired,
    onSelect: PropTypes.func.isRequired,
};

export default function BlockTypePicker({ show, onSelect, onClose }) {
    const authored = BLOCK_TYPE_LIST.filter((d) => d.category === 'authored');
    const dynamic = BLOCK_TYPE_LIST.filter((d) => d.category === 'dynamic');

    return (
        <Modal show={show} onHide={onClose} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Add a block</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h6 className="text-uppercase text-muted small mb-2">
                    Authored content
                </h6>
                <Row>
                    {authored.map((descriptor) => (
                        <BlockTypeCard
                            key={descriptor.type}
                            descriptor={descriptor}
                            onSelect={onSelect}
                        />
                    ))}
                </Row>
                <h6 className="text-uppercase text-muted small mb-2 mt-2">
                    Live ecosystem blocks
                </h6>
                <Row>
                    {dynamic.map((descriptor) => (
                        <BlockTypeCard
                            key={descriptor.type}
                            descriptor={descriptor}
                            onSelect={onSelect}
                        />
                    ))}
                </Row>
            </Modal.Body>
        </Modal>
    );
}

BlockTypePicker.propTypes = {
    // Whether the picker modal is visible.
    show: PropTypes.bool.isRequired,
    // Called with the chosen block `type`.
    onSelect: PropTypes.func.isRequired,
    // Called when the modal is dismissed without a selection.
    onClose: PropTypes.func.isRequired,
};
